import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Pencil, Plus, Radio, Trash2 } from "lucide-react";
import { api } from "@/api/client";
import type { PromptMode, PromptType, ResolvedTopicWithEstimate, Subtopic, TopicPrompt } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { EditPromptModal } from "@/components/prompts/EditPromptModal";
import { usePromptsStore } from "@/stores/prompts";
import { useBrandsStore } from "@/stores/brands";
import { LensPill } from "@/components/topics/shared";
import { cn } from "@/lib/utils";

const SENTIMENT_DIMENSIONS = ["recommendation", "sentiment", "visibility"];

/** Client-side pulse starter prompts — no LLM call, interpolated with the brand name. */
function starterPrompts(brand: string): { name: string; template: string }[] {
  return [
    { name: "Trust & reputation", template: `Is ${brand} a trustworthy brand?` },
    { name: "Quality", template: `How would you rate the quality of ${brand}?` },
    { name: "Customer service", template: `What is ${brand}'s customer service like?` },
    { name: "General reputation", template: `What do people think of ${brand}?` },
  ];
}

interface TopicPromptsTabProps {
  topicId: number;
  topicName: string;
  resolved: ResolvedTopicWithEstimate;
  onMutated: () => Promise<void>;
}

function modeSummary(prompts: TopicPrompt[]): string {
  const blind = prompts.filter((p) => p.mode === "blind").length;
  const field = prompts.filter((p) => p.mode === "field").length;
  const both = prompts.filter((p) => p.mode === "both").length;
  const parts: string[] = [];
  if (blind) parts.push(`${blind} blind`);
  if (field) parts.push(`${field} field`);
  if (both) parts.push(`${both} both`);
  return parts.join(" / ");
}

function ModeBadge({ mode }: { mode: PromptMode }) {
  if (mode === "blind") return <LensPill lens="blind" />;
  if (mode === "field") return <LensPill lens="field" />;
  return (
    <span className="inline-flex items-center gap-1">
      <LensPill lens="blind" />
      <LensPill lens="field" />
    </span>
  );
}

export function TopicPromptsTab({ topicId, topicName, resolved, onMutated }: TopicPromptsTabProps) {
  const navigate = useNavigate();
  const removePrompt = usePromptsStore((s) => s.remove);
  const fetchPrompts = usePromptsStore((s) => s.fetch);
  const brands = useBrandsStore((s) => s.brands);
  const fetchBrands = useBrandsStore((s) => s.fetch);

  // Pulse starter prompts interpolate the primary brand name — make sure it's loaded.
  useEffect(() => { if (brands.length === 0) fetchBrands(); }, []);

  const [editing, setEditing] = useState<TopicPrompt | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const prompts = resolved.prompts;
  const subtopics = resolved.subtopics;
  const isPulse = resolved.topic.kind === "pulse";
  const primaryName = brands.find((b) => b.id === resolved.primaryBrandId)?.name ?? "your brand";

  const bySubtopic = new Map<number, TopicPrompt[]>();
  const ungrouped: TopicPrompt[] = [];
  for (const p of prompts) {
    if (p.subtopic_id === null) ungrouped.push(p);
    else {
      const list = bySubtopic.get(p.subtopic_id) ?? [];
      list.push(p);
      bySubtopic.set(p.subtopic_id, list);
    }
  }

  const runMutation = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await fn();
      await onMutated();
    } catch (e: any) {
      setError(e?.message || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const setMode = (p: TopicPrompt, mode: PromptMode) =>
    runMutation(async () => { await api.prompts.update(p.id, { mode }); });

  const setSubtopic = (p: TopicPrompt, subtopicId: number | null) =>
    runMutation(async () => { await api.prompts.update(p.id, { subtopic_id: subtopicId }); });

  const remove = (p: TopicPrompt) =>
    runMutation(async () => { await removePrompt(p.id); });

  const addStarterPrompts = () =>
    runMutation(async () => {
      for (const p of starterPrompts(primaryName)) {
        // sentiment type defaults to field mode server-side, which is what pulse wants.
        await api.prompts.create(p.name, p.template, SENTIMENT_DIMENSIONS, "sentiment", topicName);
      }
      await fetchPrompts();
    });

  const renameGroup = (s: Subtopic) => {
    const name = window.prompt("Rename subtopic", s.name);
    if (!name || name.trim() === s.name) return;
    runMutation(async () => { await api.topics.renameSubtopic(s.id, name.trim()); });
  };

  const removeGroup = (s: Subtopic) => {
    if (!window.confirm(`Remove the "${s.name}" group? Its prompts stay on this topic, just ungrouped.`)) return;
    runMutation(async () => { await api.topics.removeSubtopic(s.id); });
  };

  const promoteGroup = async (s: Subtopic) => {
    if (!window.confirm(`Promote "${s.name}" to its own topic? Its prompts move there and the parent's competitors are copied.`)) return;
    setBusy(true);
    setError("");
    try {
      const topic = await api.topics.promoteSubtopic(s.id);
      await onMutated();
      navigate(`/app/topics/${topic.id}`);
    } catch (e: any) {
      setError(e?.message || "Could not promote the subtopic.");
      setBusy(false);
    }
  };

  const PromptRow = ({ p }: { p: TopicPrompt }) => (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0 flex-wrap">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13.5px] font-semibold text-brand-950">{p.name}</span>
          <Badge variant="category">{p.type === "sentiment" ? "Sentiment" : "Scoring"}</Badge>
          <ModeBadge mode={p.mode} />
        </div>
        <p className="mt-0.5 text-[12px] italic text-slate-400 line-clamp-2">“{p.template}”</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <NativeSelect
          className="h-8 w-[92px] text-xs"
          value={p.mode}
          disabled={busy}
          aria-label="Prompt mode"
          onChange={(e) => setMode(p, e.target.value as PromptMode)}
        >
          <option value="field">Field</option>
          <option value="blind">Blind</option>
          <option value="both">Both</option>
        </NativeSelect>
        <NativeSelect
          className="h-8 w-[130px] text-xs"
          value={p.subtopic_id ?? ""}
          disabled={busy}
          aria-label="Subtopic"
          onChange={(e) => setSubtopic(p, e.target.value === "" ? null : parseInt(e.target.value))}
        >
          <option value="">No subtopic</option>
          {subtopics.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </NativeSelect>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Edit prompt" onClick={() => setEditing(p)}>
          <Pencil className="w-3.5 h-3.5" />
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-500" aria-label="Delete prompt" disabled={busy} onClick={() => remove(p)}>
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  const blindCount = prompts.filter((p) => p.mode === "blind" || p.mode === "both").length;
  const fieldCount = prompts.filter((p) => p.mode === "field" || p.mode === "both").length;

  return (
    <div className="space-y-5">
      {error && <p className="text-[12.5px] text-red-600">{error}</p>}

      {prompts.length > 0 && (
        <p className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">
          {prompts.length} prompt{prompts.length === 1 ? "" : "s"} in {topicName.charAt(0).toUpperCase() + topicName.slice(1)}
          {subtopics.length > 0 ? ` · ${subtopics.length} subtopic${subtopics.length === 1 ? "" : "s"}` : ""}
          {" · "}{blindCount} blind / {fieldCount} field
        </p>
      )}

      {prompts.length === 0 && isPulse && (
        <EmptyState
          icon={Radio}
          title="Add starter prompts"
          description={`Start with four ready-made pulse questions about ${primaryName} — trust, quality, customer service, and reputation. No AI runs yet; you can edit them after.`}
          action={
            <Button onClick={addStarterPrompts} disabled={busy}>
              {busy ? "Adding…" : "Add starter prompts"}
            </Button>
          }
        />
      )}

      {prompts.length === 0 && !isPulse && (
        <p className="text-[13px] text-slate-500">No prompts on this topic yet. Add one below.</p>
      )}

      {subtopics.map((s) => {
        const group = bySubtopic.get(s.id) ?? [];
        return (
          <Card key={s.id}>
            <CardContent className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className="text-[13.5px] font-bold text-brand-950">{s.name}</span>
                <Badge variant="muted">{group.length} prompt{group.length === 1 ? "" : "s"}</Badge>
                {group.length > 0 && <span className="text-[11.5px] text-slate-400">{modeSummary(group)}</span>}
                <GroupMenu onRename={() => renameGroup(s)} onPromote={() => promoteGroup(s)} onRemove={() => removeGroup(s)} disabled={busy} />
              </div>
              {group.length === 0 ? (
                <p className="text-[12px] text-slate-400 py-1">No prompts in this group.</p>
              ) : group.map((p) => <PromptRow key={p.id} p={p} />)}
            </CardContent>
          </Card>
        );
      })}

      {ungrouped.length > 0 && (
        <Card>
          <CardContent className="space-y-1">
            {subtopics.length > 0 && (
              <div className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500 mb-1">Ungrouped</div>
            )}
            {ungrouped.map((p) => <PromptRow key={p.id} p={p} />)}
          </CardContent>
        </Card>
      )}

      {resolved.archivedPromptIds.length > 0 && (
        <p className="text-[11.5px] text-slate-400">
          {resolved.archivedPromptIds.length} archived prompt{resolved.archivedPromptIds.length === 1 ? "" : "s"} still resolve in history.
        </p>
      )}

      <AddSubtopicForm topicId={topicId} onMutated={onMutated} />
      <AddPromptForm
        topicId={topicId}
        topicName={topicName}
        subtopics={subtopics}
        onCreated={async () => { await fetchPrompts(); await onMutated(); }}
      />

      <EditPromptModal
        prompt={editing}
        open={editing !== null}
        onOpenChange={(open) => { if (!open) { setEditing(null); onMutated(); } }}
      />
    </div>
  );
}

function GroupMenu({ onRename, onPromote, onRemove, disabled }: { onRename: () => void; onPromote: () => void; onRemove: () => void; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative ml-auto">
      <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Group actions" onClick={() => setOpen((v) => !v)}>
        <MoreHorizontal className="w-4 h-4" />
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-8 z-20 w-44 rounded-md border border-slate-200 bg-white py-1 shadow-lg">
            {[
              { label: "Rename", fn: onRename },
              { label: "Promote to topic", fn: onPromote },
              { label: "Remove group", fn: onRemove },
            ].map((a) => (
              <button
                key={a.label}
                type="button"
                disabled={disabled}
                onClick={() => { setOpen(false); a.fn(); }}
                className="w-full px-3 py-1.5 text-left text-[12.5px] text-slate-600 hover:bg-slate-50 disabled:opacity-40"
              >
                {a.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function AddSubtopicForm({ topicId, onMutated }: { topicId: number; onMutated: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.topics.addSubtopic(topicId, name.trim());
      setName("");
      await onMutated();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex gap-2">
      <Input
        placeholder="Add a subtopic — e.g. pricing, support"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
      />
      <Button type="button" variant="secondary" onClick={add} disabled={!name.trim() || busy}>
        <Plus className="w-4 h-4 mr-1" />Subtopic
      </Button>
    </div>
  );
}

function AddPromptForm({ topicId, topicName, subtopics, onCreated }: {
  topicId: number;
  topicName: string;
  subtopics: Subtopic[];
  onCreated: () => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [template, setTemplate] = useState("");
  const [type, setType] = useState<PromptType>("sentiment");
  const [mode, setMode] = useState<PromptMode>("field");
  const [subtopicId, setSubtopicId] = useState<number | null>(null);
  const [dimensions, setDimensions] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => { setName(""); setTemplate(""); setType("sentiment"); setMode("field"); setSubtopicId(null); setDimensions(""); setError(""); };

  const submit = async () => {
    if (!name.trim() || !template.trim()) { setError("Name and template are required."); return; }
    let dims: string[];
    if (type === "sentiment") dims = ["recommendation", "sentiment", "visibility"];
    else {
      dims = dimensions.split(",").map((d) => d.trim()).filter(Boolean);
      if (dims.length === 0) { setError("Scoring prompts need at least one dimension."); return; }
    }
    setSaving(true);
    setError("");
    try {
      const created = await api.prompts.create(name.trim(), template.trim(), dims, type, topicName);
      if (mode !== "field" || subtopicId !== null) {
        await api.prompts.update(created.id, { mode, subtopic_id: subtopicId });
      }
      await onCreated();
      reset();
      setOpen(false);
    } catch (e: any) {
      setError(e?.message || "Could not add the prompt.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <Button variant="secondary" onClick={() => setOpen(true)}>
        <Plus className="w-4 h-4 mr-1.5" />Add prompt
      </Button>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-3">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="new-prompt-name">Name</Label>
            <Input id="new-prompt-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Trust & reputation" />
          </div>
          <div>
            <Label htmlFor="new-prompt-type">Type</Label>
            <NativeSelect id="new-prompt-type" value={type} onChange={(e) => setType(e.target.value as PromptType)}>
              <option value="sentiment">Sentiment</option>
              <option value="scoring">Scoring</option>
            </NativeSelect>
          </div>
        </div>
        <div>
          <Label htmlFor="new-prompt-template">Template</Label>
          <Textarea id="new-prompt-template" rows={4} value={template} onChange={(e) => setTemplate(e.target.value)} placeholder="Write it the way a real customer would ask. Use {brands} where the brand list goes." />
        </div>
        {type === "scoring" && (
          <div>
            <Label htmlFor="new-prompt-dims">Dimensions</Label>
            <Input id="new-prompt-dims" value={dimensions} onChange={(e) => setDimensions(e.target.value)} placeholder="Comma-separated, e.g. reputation, expertise" />
          </div>
        )}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="new-prompt-mode">Mode</Label>
            <NativeSelect id="new-prompt-mode" value={mode} onChange={(e) => setMode(e.target.value as PromptMode)}>
              <option value="field">Field</option>
              <option value="blind">Blind</option>
              <option value="both">Both</option>
            </NativeSelect>
          </div>
          <div>
            <Label htmlFor="new-prompt-subtopic">Subtopic</Label>
            <NativeSelect id="new-prompt-subtopic" value={subtopicId ?? ""} onChange={(e) => setSubtopicId(e.target.value === "" ? null : parseInt(e.target.value))}>
              <option value="">No subtopic</option>
              {subtopics.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </NativeSelect>
          </div>
        </div>
        {error && <p className="text-[12.5px] text-red-600">{error}</p>}
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => { reset(); setOpen(false); }}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving ? "Adding…" : "Add prompt"}</Button>
        </div>
      </CardContent>
    </Card>
  );
}
